import { matchPath } from 'react-router-dom';
import { type RouteConfig, type RouteRegistryEntry } from './FrontRoyalRouteRegistry.types';

function normalizePath(path: string): string {
    if (!path) return path;
    return path.startsWith('/') ? path : `/${path}`;
}

/**
 * Global registry of route metadata, keyed by path pattern.
 */
const routeRegistry = new Map<string, RouteConfig>();

/**
 * registerRoutes is used for 2 things.
 * 1. It attaches metadata to a route. This metadata can be accessed from above the FrontRoyalRouter component
 *      and is used to check the few things that are defined in the RouteConfig type.
 *      For example, the metadata defines whether the app shell should be shown on this route.
 * 2. It is used inside getRouteNameFromPath to go from a path to a nice name that can be used in event logging
 * @param entries - Array of route configuration entries
 */
export function registerRoutes(entries: RouteRegistryEntry[]): void {
    entries.forEach(entry => {
        const { routes, config } = entry;
        const { removeAppShell = false, headerLayout, errorHandlingRules } = config;

        // Extract all paths from the routes object
        extractPaths(routes).forEach(path => {
            routeRegistry.set(path, {
                removeAppShell,
                headerLayout,
                errorHandlingRules,
            });
        });
    });
}

/**
 * Recursively extract all path properties from a routes object.
 */
function extractPaths(obj: unknown, paths: string[] = []): string[] {
    if (obj && typeof obj === 'object') {
        if ('path' in obj && typeof obj.path === 'string') {
            paths.push(normalizePath(obj.path));
        }

        Object.values(obj).forEach(value => {
            extractPaths(value, paths);
        });
    }

    return paths;
}

/**
 * Get metadata for a given pathname.
 * Returns undefined if no matching route is found.
 */
export function getRouteMetadata(pathname: string): RouteConfig | undefined {
    // Try exact match first
    const exactMatch = routeRegistry.get(pathname);
    if (exactMatch) {
        return exactMatch;
    }

    // Try pattern matching for dynamic routes using React Router's matchPath, which understands
    // the :param? optional-parameter syntax natively (e.g. '/admin/mba/:section?' matches both
    // '/admin/mba' and '/admin/mba/foo').
    const entries = Array.from(routeRegistry.entries());
    const matchedEntry = entries.find(([pattern]) => matchPath(normalizePath(pattern), pathname));

    return matchedEntry ? matchedEntry[1] : undefined;
}

/**
 * Expose the internal route registry.
 * Primarily used for matching route names when exposing state to Angular.
 */
export function getRouteRegistry(): Map<string, RouteConfig> {
    return routeRegistry;
}

/**
 * Clear all registered routes from the registry.
 * Primarily used for testing.
 */
export function clearRouteRegistry(): void {
    routeRegistry.clear();
}
